import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTriangleExclamation, faCircleCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { AppDispatch, RootState } from "../../../store/indexRedux";
import { fetchPortfolioServices } from "../../../store/companyPortfolioSlice";
import { fetchUserCompany } from "../../../store/companySlice";
import GroupSelectionList from "./GroupSelectionList";
import DestinationCompanyPicker from "./DestinationCompanyPicker";
import {
  assessPoint,
  assessPointPreswitch,
  buildAddSdpsBody,
  buildCommodityPoints,
  buildTransferableFlex,
  groupEans,
  resolveSwitchGroup,
} from "./switchGroup";
import type { CommodityPoint, PointBlockReason } from "./switchGroup";
import { useGroupPreswitch } from "../../../hooks/useGroupPreswitch";
import {
  DestinationReadiness,
  fetchDestinationReadiness,
} from "../../../api/customerSwitchTransfer";
import type { Company } from "../../../types/companyTypes";
import { CustomerSwitchModal } from "./CustomerSwitchModal";

/**
 * Customer Switch, Manage tab.
 *
 * The DIRECTION IS INVERTED relative to the onboarding wizard. There, an admin stood
 * in the RECEIVING company and typed an EAN that the backend discovered was held by
 * somebody else. Here the user stands in the company that HOLDS the points
 * (company A, `sourceCompanyId`) and picks the destination (company B,
 * `destinationCompanyId`) explicitly.
 *
 * 🔴 The two ids were ONE value in the wizard and are not any more. Every call keeps
 * using company B except the preswitch, which uses company A because A's SDP is the
 * one that exists. Nothing on this page is named a bare `companyId`.
 *
 * ONE GROUP PER TRANSFER: a connection point plus, when a flex site links them, its
 * sibling point and every flex contract on that site. To move more, run it again.
 */
const CustomerSwitchTransfer: React.FC = () => {
  const { t } = useTranslation("management");
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const token = useSelector((state: RootState) => state.authv2.accessToken);
  const sourceCompany = useSelector(
    (state: RootState) => state.selectedCompany.selectedUserCompany
  );
  const sourceCompanyId = sourceCompany?.company_id ?? undefined;
  /**
   * The KBO/company number — a DIFFERENT id space from `company_id` above. This is
   * what `/allservices` is queried by, so it is the only value portfolio rows can be
   * compared against. Keep the two visibly distinct: they happen to hold equal values
   * today, which is exactly what makes a mix-up invisible.
   */
  const sourceCompanyNumber = sourceCompany?.companyNumber;
  const portfolio = useSelector((state: RootState) => state.companyPortfolio.data);
  const portfolioLoading = useSelector((state: RootState) => state.companyPortfolio.loading);
  const portfolioError = useSelector((state: RootState) => state.companyPortfolio.error);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [destination, setDestination] = useState<Company | null>(null);
  const [readiness, setReadiness] = useState<DestinationReadiness | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessError, setReadinessError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  /**
   * 🔴 A CAPTURED SNAPSHOT, never a read of `destination`.
   *
   * Switching into company B changes `sourceCompanyId`, and the reset effect below
   * is keyed on exactly that — so it wipes `destination` the moment the switch
   * lands. Reading `destination` in the offer, or in the switch handler, would
   * therefore read `null` precisely when it matters. `{id, name}` is copied out at
   * the moment the transfer completes and nothing else can touch it.
   */
  const [completed, setCompleted] = useState<{ id: number; name: string } | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);

  const { entries, ensure, retry } = useGroupPreswitch(sourceCompanyId, token ?? undefined);

  /**
   * 🔴 PROVENANCE, NOT PRESENCE. The portfolio must be PROVABLY the selected
   * company's before a single row of it is acted on.
   *
   * `CompanyServices` carries no company identifier, and the slice is never cleared
   * on a company switch: `resetState` exists but is dispatched NOWHERE, `pending`
   * does not clear `data`, `rejected` does not clear `data`, and App.tsx's refetch is
   * gated on `selectedUserCompany?.vatCode` so a company with a falsy vatCode never
   * refetches at all. Three independent paths therefore leave the PREVIOUS company's
   * portfolio in the store while the header says you are standing somewhere else.
   *
   * A `if (portfolio) return;` backstop tests presence and is satisfied by exactly
   * that stale data — so ticking a point fired
   * `preswitchLigt/{selectedCompanyId}/ADD_PSL/{otherCompanysEan}`.
   *
   * The harm is SELECTION CORRECTNESS, not storage: which company the preswitch row
   * lands under does not matter (product ruling). What matters is that the user was
   * shown, and could act on, connection points that are not this company's. That is
   * the FT-3581 shape — acting on the wrong company's data because a guard checked
   * the wrong thing.
   *
   * The ROWS do carry `company_id` (InjectionService / OfftakeService / FlexService),
   * so provenance is derivable from the payload itself rather than trusted. Any
   * foreign row disqualifies the whole payload — a mixed response is not something to
   * partially salvage. An EMPTY portfolio is unprovable but harmless: there is
   * nothing to tick, so no request can be built from it.
   *
   * 🔴 COMPARE AGAINST THE ID SPACE THE ROWS WERE SELECTED BY — `companyNumber`, not
   * the `company_id` PK. `/allservices/:companyId` is called with `companyNumber`
   * (see the fetch below) and the backend filters `WHERE si.company_id = ?` on that
   * same value, so every row it returns carries `company_id == companyNumber`.
   * Comparing them against the PK instead only works while the two columns hold
   * equal values for a company — true for all 5077 today, but nothing in the schema
   * or the code enforces it.
   *
   * The failure if they ever diverge is silent and total: every row reads as foreign,
   * the portfolio is discarded as stale, and that company's page is permanently dead
   * — no points, no error, no retry, no explanation. Matching the id space removes
   * the landmine instead of documenting it.
   */
  const ownedPortfolio = useMemo(() => {
    if (!portfolio || !sourceCompanyNumber) return null;
    const rows = [
      ...portfolio.services.injection,
      ...portfolio.services.offtake,
      ...portfolio.services.bess,
      ...portfolio.services.eboiler,
      ...portfolio.services.pvCurtailment,
    ];
    const hasForeignRow = rows.some(
      (r) => r.company_id != null && Number(r.company_id) !== Number(sourceCompanyNumber)
    );
    return hasForeignRow ? null : portfolio;
  }, [portfolio, sourceCompanyNumber]);

  /** Data is present but belongs to someone else → a refetch is owed, not an empty list. */
  const portfolioIsStale = !!portfolio && ownedPortfolio === null;

  const points = useMemo(() => buildCommodityPoints(ownedPortfolio), [ownedPortfolio]);
  const flexAssets = useMemo(() => buildTransferableFlex(ownedPortfolio), [ownedPortfolio]);

  /**
   * The portfolio is the selection source. App.tsx fetches it on company change, but
   * a reload straight into this route can arrive before that lands, so this is a
   * backstop.
   *
   * 🔴 GATED ON A REF, NOT ON THE SLICE. The obvious guard —
   * `!portfolio && !portfolioLoading` — is gated on the very values its own dispatch
   * writes, and the slice's `rejected` reducer sets `loading = false` and `error`
   * WITHOUT touching `data`. So after any rejection (transient 5xx, expired token, a
   * company with no portfolio row) the guard is true again on the very next render
   * and it re-dispatches forever, with no backoff.
   *
   * Compare App.tsx's own fetch: that one is gated on `selectedUserCompany`
   * IDENTITY, which the fetch never mutates. This ref reproduces that property —
   * one dispatch per companyNumber, decided by something the request cannot change.
   *
   * ⚠️ TWO INDEPENDENT HALVES, ONE LOOP — the same caveat as the `group` memo above,
   * and the second instance of it in this file. Dropping `portfolioLoading` from the
   * dep array is BY ITSELF sufficient (the effect then never re-arms); keeping the
   * shipped deps and removing the ref is ALSO sufficient. Each half alone holds the
   * line, so the regression pin — which drives the real page and counts requests —
   * stays GREEN if a future refactor removes either one on its own. Only removing
   * BOTH turns it red. If you take one out you are relying entirely on the other:
   * do that deliberately, or not at all.
   */
  const portfolioRequestedFor = useRef<string | null>(null);
  useEffect(() => {
    const companyNumber = sourceCompany?.companyNumber;
    if (!companyNumber || !token) return;
    // `ownedPortfolio`, never `portfolio`: holding another company's data is a
    // reason to refetch, not a reason to skip.
    if (ownedPortfolio) return;
    if (portfolioRequestedFor.current === companyNumber) return;
    portfolioRequestedFor.current = companyNumber;
    dispatch(fetchPortfolioServices({ companyNumber, token }));
  }, [ownedPortfolio, sourceCompany?.companyNumber, token, dispatch]);

  /**
   * COMPANY A CHANGED → reset everything. The header switcher is reachable from every
   * step, and the selection, the group and the destination all belong to the previous
   * company. Company A is never sent on the wire (the backend derives it from the
   * point's own foreign rows), so a stale selection under a new A would be performed
   * and look entirely correct.
   */
  useEffect(() => {
    setSelectedKey(null);
    setDestination(null);
    setReadiness(null);
    setReadinessError(null);
    setModalOpen(false);
    setCompleted(null);
    setSwitchError(null);
  }, [sourceCompanyId]);



  const groupFor = useCallback(
    (key: string) => resolveSwitchGroup({ primaryKey: key, points, flex: flexAssets }),
    [points, flexAssets]
  );

  /**
   * 🔴 TWO INDEPENDENT GUARDS PROTECT ONE LOOP. Keep BOTH.
   *
   * `resolveSwitchGroup` returns a fresh object literal, so an unmemoized `group`
   * has a new identity every render. The readiness effect below calls setState, so
   * if it also depends on that identity you get
   * effect -> setState -> render -> new identity -> effect: an unbounded
   * GET /billing-info loop on the plain happy path.
   *
   * Measured, all four arms:
   *
   *     memo      deps        requests
   *     memo      primitive   1
   *     NO memo   primitive   1
   *     memo      object      1
   *     NO memo   object      1917 in ~1s
   *
   * So each half is individually SUFFICIENT and neither is individually NECESSARY.
   * The memo also keeps the derived values below cheap and stable, which is why it
   * stays even though the primitive deps alone would hold the line.
   *
   * ⚠️ THE REGRESSION TEST CANNOT CATCH A SINGLE-HALF REMOVAL. It exercises the
   * real page, so dropping either guard on its own leaves it green — only removing
   * BOTH turns it red. A future refactor can therefore delete one of these
   * silently. If you take one out, you are relying entirely on the other; do it
   * deliberately or not at all.
   */
  const group = useMemo(
    () => (selectedKey ? resolveSwitchGroup({ primaryKey: selectedKey, points, flex: flexAssets }) : null),
    [selectedKey, points, flexAssets]
  );
  /**
   * The EANs to look up. EMPTY for a BLOCKED group.
   *
   * 🔴 THIS FILTER IS NOT REDUNDANT WITH THE DESTINATION GUARD BELOW, and it was
   * once removed on the reasoning that it had become unreachable. It had not. The
   * surviving path: select a clean group, pick a destination, THEN select a blocked
   * point. `toggle` changes only `selectedKey` — `setDestination(null)` happens ONLY
   * on a company change and in `clearSelection` — so `destination` outlives the
   * selection that produced it, the picker unmounts, `if (!destination) return`
   * passes, and nothing else stops the lookup.
   *
   * The two guards protect DIFFERENT things and neither implies the other: this one
   * says "this group can never transfer", the other says "there is no destination
   * yet". Belt-and-braces is correct here.
   *
   * GENERAL LESSON, which is the transferable part: a guard is only dead when EVERY
   * path to it is closed. Deadness derived from the happy path alone is a guess —
   * state that outlives the interaction which produced it (as `destination` outlives
   * a selection) is exactly the kind of surviving path that makes "obviously
   * unreachable" wrong.
   */
  const eansInGroup = useMemo(
    () => (group && !group.blocked ? groupEans(group) : []),
    [group]
  );

  /**
   * Preswitch the group's points — AS SOON AS THE EAN IS SELECTED.
   *
   * 🔴 THE DESTINATION GUARD IS GONE, DELIBERATELY. It used to sit here on the
   * reasoning that ADD_PSL is a real Atrias round trip plus our own INSERT into
   * `mig6_messages_preswitchligt`, so browsing a selection should cost nothing. The
   * cost is real and is now ACCEPTED: four of the nine refusal reasons
   * (`preswitchFailed`, `unsupportedMeter`, `capabilityUnknown`, `runningProcess`)
   * are properties of the POINT, and deferring them meant a user picked a point,
   * hunted down a receiving company, and only then learned the point was never
   * transferable. One call per first tick of a point buys that answer immediately.
   *
   * WHAT STILL BOUNDS IT, and none of it may be removed:
   *   - `eansInGroup` is EMPTY for a blocked group (`group && !group.blocked`), so a
   *     row we already know cannot transfer still triggers nothing. That filter is
   *     now the ONLY guard on this path — read its note above before touching it.
   *   - the hook's `attempted` ref caps this at ONE attempt per EAN per company
   *     selection, failures included. Deselect/reselect re-runs this effect and
   *     issues nothing.
   *
   * LOOP LENS (the two-condition rule): (a) the guard can still be true after a
   * failure — `attempted` in the hook makes that terminal — and (b) NO value in this
   * dep array oscillates on the failure path. `eansInGroup` derives from the
   * selection (never from `entries`), and `ensure` is identity-stable (deps
   * `[fetchOne]`, itself `[sourceCompanyId, token]`). The lookup writes only
   * `entries`, which is in neither the guard nor the deps.
   */
  useEffect(() => {
    if (eansInGroup.length === 0) return;
    void ensure(eansInGroup);
  }, [eansInGroup, ensure]);

  const pslState = useCallback(
    (ean: string): "idle" | "loading" | "ready" | "error" => entries.get(ean)?.state ?? "idle",
    [entries]
  );

  /**
   * The portfolio row behind an EAN, for the Walloon stored-meter-type rule. The
   * meter gate and the addSdps body BOTH read it, so it is resolved once.
   */
  const pointByKey = useMemo(() => {
    const out = new Map<string, CommodityPoint>();
    for (const p of points) out.set(p.key, p);
    return out;
  }, [points]);

  /**
   * PHASE 1 — the reasons decidable from the preswitch ALONE. Available the moment
   * a lookup lands, which is now at SELECTION time, so a point that can never be
   * transferred says so before the user goes looking for a receiving company.
   */
  const preswitchBlocks = useMemo(() => {
    const out = new Map<string, PointBlockReason>();
    for (const ean of eansInGroup) {
      const entry = entries.get(ean);
      if (entry?.state !== "ready") continue;
      const reason = assessPointPreswitch({
        psl: entry.data,
        point: pointByKey.get(ean) ?? null,
      });
      if (reason) out.set(ean, reason);
    }
    return out;
  }, [eansInGroup, entries, pointByKey]);

  /**
   * PHASE 2 — the full assessment, including the reasons that mention company B.
   * Still destination-gated, and it is the ONLY source of `legs`.
   */
  const assessments = useMemo(() => {
    const out = new Map<string, ReturnType<typeof assessPoint>>();
    if (!group || !destination) return out;
    for (const ean of eansInGroup) {
      const entry = entries.get(ean);
      if (entry?.state !== "ready") continue;
      out.set(
        ean,
        assessPoint({
          psl: entry.data,
          destinationCompanyId: destination.id,
          isGroupMember: ean !== group.primary.key,
          point: pointByKey.get(ean) ?? null,
        })
      );
    }
    return out;
  }, [group, destination, eansInGroup, entries, pointByKey]);

  /**
   * Both phases, merged — the ONE map the row copy is driven from.
   *
   * `assessPoint` runs phase 1 first, so where the two overlap they agree by
   * construction; phase 2 only ADDS the destination-dependent reasons on top once
   * a destination exists.
   */
  const pointBlocks = useMemo(() => {
    const out = new Map<string, PointBlockReason>(preswitchBlocks);
    for (const [ean, assessment] of assessments) {
      if (assessment.blocked) out.set(ean, assessment.blocked);
    }
    return out;
  }, [preswitchBlocks, assessments]);

  const blockReason = useCallback(
    (key: string): string | null => {
      const g = groupFor(key);
      if (g?.blocked) return t(`customerSwitchBlocked_${g.blocked}`);
      // Point-level reasons need the preswitch. The four point-only ones appear as
      // soon as it lands; the rest once a destination is known too.
      for (const [ean, reason] of pointBlocks) {
        return ean === key
          ? t(`customerSwitchBlocked_${reason}`)
          : t("customerSwitchBlockedMember", { ean, reason: t(`customerSwitchBlocked_${reason}`) });
      }
      return null;
    },
    [groupFor, pointBlocks, t]
  );

  /** Stable scalars for the readiness effect — see its dependency note. */
  const primaryEan = group?.primary.ean ?? null;
  const primaryDirection: "injection" | "offtake" | "combined" | null = group
    ? group.primary.directions.length === 2
      ? "combined"
      : group.primary.directions[0]
    : null;

  // Destination readiness — checked ONCE when B is picked, not per point at commit.
  useEffect(() => {
    if (!destination || !token || !primaryEan || !primaryDirection) {
      setReadiness(null);
      setReadinessError(null);
      return;
    }
    let cancelled = false;
    setReadinessLoading(true);
    setReadinessError(null);
    fetchDestinationReadiness(destination.id, token, {
      ean: primaryEan,
      direction: primaryDirection,
    })
      .then((result) => {
        if (!cancelled) setReadiness(result);
      })
      .catch((err) => {
        if (cancelled) return;
        setReadiness(null);
        setReadinessError(
          axios.isAxiosError(err) && err.response?.status === 404
            ? t("customerSwitchCompanyNotFound")
            : t("customerSwitchCompanyCheckFailed")
        );
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Deps are PRIMITIVES on purpose — never `group` or `destination` by identity.
    // The memo above makes those stable today, but an effect that calls setState
    // must not be one refactor away from re-entering itself. exhaustive-deps wants
    // the `destination` OBJECT here; taking it is what caused the request loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination?.id, token, primaryEan, primaryDirection, t]);

  /** The acting user — NOT either company. Feeds `signatureName` into the docx. */
  const user = useSelector((state: RootState) => state.user.user);
  const signerName = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();

  /**
   * Everything the popup needs, derived per point from its OWN preswitch response.
   *
   * 🔴 `companyEmail` and `companyName` are company B's, NOT redux. In the wizard they
   * came from `selectedUserCompany` and happened to be B because the admin stood in B.
   * Here redux holds company A, so reading it would put A's address on B's proposal.
   */
  const modalProps = useMemo(() => {
    if (!group || !destination) return null;
    const primaryEntry = entries.get(group.primary.key);
    if (primaryEntry?.state !== "ready") return null;
    const primaryAssessment = assessments.get(group.primary.key);
    if (!primaryAssessment || primaryAssessment.blocked || primaryAssessment.legs.length === 0) {
      return null;
    }
    const memberPoint = group.member;
    const memberEntry = memberPoint ? entries.get(memberPoint.key) : undefined;
    const memberAssessment = memberPoint ? assessments.get(memberPoint.key) : undefined;
    if (memberPoint && (memberEntry?.state !== "ready" || !memberAssessment || memberAssessment.blocked)) {
      return null;
    }
    const memberLeg = memberAssessment?.legs[0];
    /**
     * 🔴 THE STORED METER TYPE HAS TO TRAVEL WITH THE POINT.
     *
     * `pslFullData` is posted VERBATIM as the `POST /sdp/addSdps/{B}` body and is
     * this modal's ONLY consumer of it (grep: the modal never reads a field off
     * it). The backend writes `dataPSL.ean.typeOfMeter` straight into
     * `sdps_*.meterType`, so without this a Walloon point would be allowed through
     * the gate and then have the very MMR that used to block it persisted under
     * company B — blocked again from the other side, with a self-inflicted cause.
     *
     * `buildAddSdpsBody` returns the response UNTOUCHED (same object identity)
     * outside Wallonia and whenever there is no better stored value, so every
     * non-Walloon transfer is byte-identical to what ships today.
     */
    return {
      ean: group.primary.ean,
      destinationCompanyId: destination.id,
      pslFullData: buildAddSdpsBody(primaryEntry.data, group.primary),
      legs: primaryAssessment.legs,
      companyEmail: readiness?.companyEmail ?? "",
      companyName: destination.name,
      sdpAtriasConfig: primaryEntry.data.dataPSL?.ean?.sdpAtriasConfig,
      technologies: primaryEntry.data.dataPSL?.ean?.technologies,
      addressLine: primaryEntry.data.dataPSL?.ean ?? null,
      groupMembers:
        memberPoint && memberEntry?.state === "ready" && memberLeg
          ? [
              {
                ean: memberPoint.ean,
                pslFullData: buildAddSdpsBody(memberEntry.data, memberPoint),
                injectionServiceConfig: memberLeg.injectionServiceConfig ?? null,
                offtakeServiceConfig: memberLeg.offtakeServiceConfig ?? null,
                // 🔴 ONE SOURCE. `directions` used to come from the PORTFOLIO row
                // while the configs came from the PRESWITCH leg. Those are two
                // different reads of the same fact, and when they disagreed the
                // write-verification expected a direction the backend was never
                // asked for — throwing ContractNotCreatedError AFTER the commit,
                // i.e. reporting failure on a transfer that actually succeeded.
                // Both now derive from the leg the preswitch produced.
                directions:
                  memberLeg.direction === "combined"
                    ? (["injection", "offtake"] as const).slice()
                    : [memberLeg.direction],
              },
            ]
          : [],
    };
  }, [group, destination, entries, assessments, readiness]);

  /**
   * The way out of a failed portfolio load. `portfolioRequestedFor` deliberately
   * pins one dispatch per company, which is what stops the rejected-state loop — so
   * a user-initiated retry has to clear that pin explicitly. Nothing a render does
   * can reach this.
   */
  const retryPortfolio = useCallback(() => {
    const companyNumber = sourceCompany?.companyNumber;
    if (!companyNumber || !token) return;
    portfolioRequestedFor.current = null;
    dispatch(fetchPortfolioServices({ companyNumber, token }));
  }, [sourceCompany?.companyNumber, token, dispatch]);

  /**
   * Post-completion: stand in company B instead of company A.
   *
   * Reuses `SelectCompanyPage.handleSelectCompany`'s mechanism (`fetchUserCompany`
   * + `localStorage`) with two DELIBERATE divergences:
   *
   * 🔴 NO `resetCompanyState()` FIRST. This page sits under `CompanyProtectedRoute`,
   * which redirects to `/` the instant `selectedUserCompany` is null — which is
   * exactly what the reset produces. `/`'s own mount effect then clears
   * localStorage, so the user would be ejected from the flow mid-switch. Skipping it
   * means A stays selected until B's fetch fulfils, so there is never a null window.
   *
   * 🔴 NO `window.location.reload()`. `App.tsx:149-160` already refetches everything
   * on a `selectedUserCompany` identity change; a reload is a slower way to reach
   * the same state and throws away the router history.
   *
   * `selectedCompanyId` is written AFTER `fulfilled`, so a failed fetch cannot leave
   * localStorage pointing at B while redux still holds A.
   *
   * EVENT HANDLER, never an effect: it dispatches, and its result changes
   * `sourceCompanyId`, which the reset effect keys on. As an effect that is the
   * (a)+(b) shape outright.
   */
  const switchToCompletedCompany = async () => {
    if (!completed || !token || switching) return;
    setSwitching(true);
    setSwitchError(null);
    try {
      const result = await dispatch(fetchUserCompany({ company_id: completed.id, token }));
      if (result.meta.requestStatus !== "fulfilled") {
        setSwitchError(t("customerSwitchSwitchFailed"));
        return;
      }
      localStorage.setItem("selectedCompanyId", String(completed.id));
      navigate("/overview");
    } finally {
      setSwitching(false);
    }
  };

  const toggle = (key: string) => setSelectedKey((prev) => (prev === key ? null : key));

  const clearSelection = () => {
    setSelectedKey(null);
    setDestination(null);
    setReadiness(null);
    setReadinessError(null);
    setModalOpen(false);
  };

  const groupIsClean = !!group && group.blocked === null;
  const allPreswitchReady =
    eansInGroup.length > 0 && eansInGroup.every((e) => entries.get(e)?.state === "ready");
  const noPointBlocked = [...assessments.values()].every((a) => a.blocked === null);
  const billingBlocks = readiness?.hasBilling === false;

  const canCreate =
    groupIsClean &&
    allPreswitchReady &&
    noPointBlocked &&
    !!destination &&
    !readinessLoading &&
    !billingBlocks &&
    modalProps !== null;

  return (
    <div className="my-20 md:mx-14 text-primary-600 dark:text-primary-300">
      <button
        onClick={() => navigate("/management")}
        className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-600 dark:text-primary-300 dark:hover:text-white mb-6 transition-colors"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t("backToManagement")}
      </button>
      <h1 className="text-3xl font-semibold dark:text-white mb-4">
        {t("customerSwitchTitle")}
      </h1>
      <p className="max-w-2xl text-primary-400 dark:text-primary-300 mb-10">
        {t("customerSwitchIntro", { companyName: sourceCompany?.companyName ?? "" })}
      </p>

      <div className="max-w-2xl">
        <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
          <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-4">
            {t("customerSwitchSelectLabel")}
          </h3>
          <GroupSelectionList
            points={points}
            groupFor={groupFor}
            selectedKey={selectedKey}
            onToggle={toggle}
            onClear={clearSelection}
            loading={portfolioLoading || portfolioIsStale}
            onRetryPortfolio={retryPortfolio}
            portfolioError={portfolioError}
            pslState={pslState}
            blockReason={blockReason}
            onRetryPreswitch={retry}
            awaitingDestination={!destination}
          />
        </div>

        {/* Progressive disclosure: the destination only matters once something is
            selected, and the confirmation only once a destination exists. */}
        {selectedKey && groupIsClean && (
          <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
            <h3 className="font-semibold text-primary-600 dark:text-white text-lg mb-1">
              {t("customerSwitchDestinationLabel")}
            </h3>
            <p className="text-sm text-primary-400 dark:text-primary-500 mb-4">
              {t("customerSwitchDestinationHelp")}
            </p>
            {sourceCompanyId != null && (
              <DestinationCompanyPicker
                sourceCompanyId={sourceCompanyId}
                // companyNumber, NOT the PK — the space `kbo.number` lives in. Already
                // computed above for the portfolio provenance guard; it was simply not
                // being passed down.
                sourceCompanyNumber={Number(sourceCompanyNumber)}
                selected={destination}
                onSelect={setDestination}
                readiness={readiness}
                readinessLoading={readinessLoading}
                readinessError={readinessError}
              />
            )}
          </div>
        )}

        {destination && groupIsClean && (
          <div className="p-6 rounded-lg border-[1px] border-base-200 dark:border-primary-800 bg-base-100 dark:bg-primary-900 mb-6">
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-300 flex gap-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" />
              <span>{t("customerSwitchProposalsNote", { companyName: destination.name })}</span>
            </div>
          </div>
        )}

        {/* Done. `onSuccess` fires ONLY from the done-phase button
            (CustomerSwitchModal.tsx:1285), and `dismissable` is limited to
            loadingPreview/preview/error — so a dismissed modal can never open this
            offer for a transfer that did not happen. */}
        {completed && (
          <div className="p-6 rounded-lg border-[1px] border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 mb-6">
            <p className="text-sm text-green-800 dark:text-green-300 inline-flex items-start gap-2">
              <FontAwesomeIcon icon={faCircleCheck} className="mt-0.5 shrink-0" />
              <span>{t("customerSwitchTransferDone", { companyName: completed.name })}</span>
            </p>
            <p className="mt-3 text-sm text-primary-500 dark:text-primary-300">
              {t("customerSwitchSwitchOffer", { companyName: completed.name })}
            </p>
            {switchError && (
              <p className="mt-3 text-sm text-red-700 dark:text-red-300">{switchError}</p>
            )}
            <div className="mt-4 flex items-center gap-3">
              <button
                className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
                disabled={switching}
                onClick={() => void switchToCompletedCompany()}
              >
                {switching && <FontAwesomeIcon icon={faSpinner} spin />}
                {t("customerSwitchSwitchToCompany", { companyName: completed.name })}
              </button>
              <button
                className="px-5 py-2 text-sm font-semibold text-primary-500 dark:text-primary-300 underline disabled:opacity-50"
                disabled={switching}
                onClick={() => navigate("/management")}
              >
                {t("customerSwitchStayHere")}
              </button>
            </div>
          </div>
        )}

        {!completed && (
          <div className="flex items-center gap-3">
            <button
              className="px-5 py-2 bg-primary-600 text-white rounded-3xl text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              disabled={!canCreate}
              onClick={() => setModalOpen(true)}
            >
              {t("customerSwitchCreateProposals")}
            </button>
          </div>
        )}
      </div>

      {/* The existing Customer Switch popup, re-hosted. Its phase machine —
          preview -> running -> ask -> second leg -> done — is unchanged; the only
          additions are the destination id and the group members. */}
      {modalOpen && modalProps && (
        <CustomerSwitchModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={() => {
            setModalOpen(false);
            // Snapshot NOW, from modalProps — the values the transfer actually used.
            // `destination` is about to be wiped by the reset effect if the user
            // takes the offer.
            setCompleted({ id: modalProps.destinationCompanyId, name: modalProps.companyName });
          }}
          ean={modalProps.ean}
          destinationCompanyId={modalProps.destinationCompanyId}
          pslFullData={modalProps.pslFullData}
          groupMembers={modalProps.groupMembers}
          legs={modalProps.legs}
          signerName={signerName}
          companyEmail={modalProps.companyEmail}
          sdpAtriasConfig={modalProps.sdpAtriasConfig}
          technologies={modalProps.technologies}
          companyName={modalProps.companyName}
          addressLine={modalProps.addressLine}
        />
      )}
    </div>
  );
};

export default CustomerSwitchTransfer;
